import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatPoints } from "@/lib/format";
import { Crown, Sparkles, X as XIcon, Trophy } from "lucide-react";
import CommissionSettingsCard from "./CommissionSettingsCard";

export default function AdminAmbassadorsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ambassadors");
            setRows(data);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const top20Cutoff = Math.max(1, Math.ceil(rows.length * 0.2));

    return (
        <div>
            <CommissionSettingsCard />

            <div className="mb-5 rounded-2xl border border-p2w-purple/30 bg-gradient-to-br from-p2w-surface to-p2w-elevated p-5 relative overflow-hidden">
                <div className="glow-orb bg-p2w-purple w-[260px] h-[260px] -top-20 -right-10 opacity-25" />
                <div className="relative flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl border border-p2w-purple/40 bg-p2w-purple/15 flex items-center justify-center">
                        <Crown className="w-5 h-5 text-p2w-purple" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-sora font-bold text-lg">Top Embajadores · Solo admin</h3>
                        <p className="text-sm text-p2w-gray mt-1 max-w-2xl">
                            Ranking confidencial por puntos generados vía referidos. Identifica el 20% que trae el 80% del crecimiento y otórgales multiplicadores temporales como recompensa.
                        </p>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-p2w-line bg-p2w-surface">
                <table className="w-full text-sm">
                    <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Pos</th>
                            <th className="text-left px-4 py-3">Embajador</th>
                            <th className="text-right px-4 py-3">L1</th>
                            <th className="text-right px-4 py-3">L2</th>
                            <th className="text-right px-4 py-3">L3</th>
                            <th className="text-right px-4 py-3">Total generado</th>
                            <th className="text-left px-4 py-3">Boost</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (<tr><td colSpan={8} className="text-center text-p2w-gray py-10">Cargando…</td></tr>)}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-p2w-gray py-10">Aún no hay embajadores con ganancias por referidos.</td></tr>
                        )}
                        {!loading && rows.map((r) => (
                            <AmbassadorRow key={r.user_id} r={r} elite={r.rank <= top20Cutoff} onChange={load} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AmbassadorRow({ r, elite, onChange }) {
    return (
        <tr className="border-t border-p2w-line" data-testid={`ambassador-row-${r.username}`}>
            <td className="px-4 py-3">
                <RankBadge rank={r.rank} elite={elite} />
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <Avatar className="w-9 h-9">
                        <AvatarImage src={r.avatar_url} />
                        <AvatarFallback className="bg-p2w-purple text-white text-xs">
                            {(r.username || "?").slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                    <div>
                        <div className="text-white font-medium flex items-center gap-2">
                            @{r.username}
                            {elite && <Badge className="bg-p2w-amber/15 text-p2w-amber border border-p2w-amber/40 text-[9px] uppercase tracking-wider">Élite</Badge>}
                        </div>
                        <div className="text-xs text-p2w-gray">{r.direct_referrals} directos · Lv {r.level}</div>
                    </div>
                </div>
            </td>
            <td className="px-4 py-3 text-right number-tabular">
                <div className="text-p2w-green font-sora font-bold">+{formatPoints(r.l1_earnings)}</div>
                <div className="text-[10px] text-p2w-gray">{r.l1_count}× ref</div>
            </td>
            <td className="px-4 py-3 text-right number-tabular">
                <div className="text-p2w-purple font-sora font-bold">+{formatPoints(r.l2_earnings)}</div>
                <div className="text-[10px] text-p2w-gray">{r.l2_count}× ref</div>
            </td>
            <td className="px-4 py-3 text-right number-tabular">
                <div className="text-p2w-magenta font-sora font-bold">+{formatPoints(r.l3_earnings)}</div>
                <div className="text-[10px] text-p2w-gray">{r.l3_count}× ref</div>
            </td>
            <td className="px-4 py-3 text-right">
                <div className="font-sora font-extrabold text-2xl text-p2w-green number-tabular">
                    {formatPoints(r.total_earnings)}
                </div>
                <div className="text-[10px] text-p2w-gray">puntos</div>
            </td>
            <td className="px-4 py-3">
                {r.multiplier_active ? (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-p2w-amber/15 border border-p2w-amber/40">
                        <Sparkles className="w-3 h-3 text-p2w-amber" />
                        <span className="text-p2w-amber font-sora font-bold text-xs number-tabular">
                            {r.multiplier_value}x
                        </span>
                        <span className="text-[10px] text-p2w-gray">
                            hasta {new Date(r.multiplier_expires_at).toLocaleDateString()}
                        </span>
                    </div>
                ) : (
                    <span className="text-xs text-p2w-gray">—</span>
                )}
            </td>
            <td className="px-4 py-3 text-right">
                <div className="flex items-center gap-2 justify-end">
                    <GrantMultiplierDialog user={r} onDone={onChange} />
                    {r.multiplier_active && (
                        <button
                            data-testid={`revoke-multiplier-${r.username}`}
                            onClick={async () => {
                                try {
                                    await api.delete(`/admin/users/${r.user_id}/multiplier`);
                                    toast.success("Multiplicador revocado");
                                    onChange?.();
                                } catch (e) {
                                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
                                }
                            }}
                            className="p-2 rounded-lg text-p2w-red hover:bg-p2w-red/10 transition"
                            title="Revocar multiplicador"
                        >
                            <XIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
}

function RankBadge({ rank, elite }) {
    const cls =
        rank === 1
            ? "bg-p2w-green text-p2w-green-fg shadow-glow-green"
            : rank === 2
            ? "bg-white/80 text-p2w-bg"
            : rank === 3
            ? "bg-p2w-amber text-p2w-bg"
            : elite
            ? "bg-p2w-purple/20 text-p2w-purple border border-p2w-purple/40"
            : "bg-p2w-elevated text-p2w-gray border border-p2w-line";
    return (
        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-sora font-bold text-sm number-tabular ${cls}`}>
            {rank === 1 ? <Trophy className="w-4 h-4" /> : rank}
        </div>
    );
}

function GrantMultiplierDialog({ user, onDone }) {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState("2");
    const [days, setDays] = useState("7");
    const [reason, setReason] = useState("Recompensa Top Embajador");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        const v = Number(value);
        const d = Number(days);
        if (!(v > 1) || !(d > 0)) {
            toast.error("Valores inválidos");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/admin/users/${user.user_id}/grant-multiplier`, {
                value: v,
                days: d,
                reason,
            });
            toast.success(`Multiplicador ${v}x activo por ${d} días`);
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    data-testid={`grant-multiplier-${user.username}`}
                    className="bg-p2w-amber text-p2w-bg hover:bg-p2w-amber/90 h-8 font-semibold gap-1.5"
                >
                    <Sparkles className="w-3.5 h-3.5" />
                    {user.multiplier_active ? "Editar boost" : "Otorgar boost"}
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                <DialogHeader>
                    <DialogTitle className="font-sora flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-p2w-amber" />
                        Otorgar multiplicador · @{user.username}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <p className="text-sm text-p2w-gray">
                        El usuario recibirá <span className="text-white font-semibold">N×</span> en cada bonus de referido durante el periodo activo. Aplica a todos los niveles (L1, L2, L3).
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">Multiplicador</Label>
                            <Select value={value} onValueChange={setValue}>
                                <SelectTrigger data-testid="multiplier-value-select" className="mt-2 bg-p2w-elevated border-p2w-line text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                    <SelectItem value="1.5">1.5×</SelectItem>
                                    <SelectItem value="2">2×</SelectItem>
                                    <SelectItem value="3">3×</SelectItem>
                                    <SelectItem value="5">5×</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-p2w-gray text-xs uppercase tracking-widest">Duración</Label>
                            <Select value={days} onValueChange={setDays}>
                                <SelectTrigger data-testid="multiplier-days-select" className="mt-2 bg-p2w-elevated border-p2w-line text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                                    <SelectItem value="3">3 días</SelectItem>
                                    <SelectItem value="7">7 días</SelectItem>
                                    <SelectItem value="14">14 días</SelectItem>
                                    <SelectItem value="30">30 días</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Motivo</Label>
                        <Input
                            data-testid="multiplier-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                        />
                    </div>
                    <div className="rounded-lg bg-p2w-elevated border border-p2w-line p-3 text-xs text-p2w-gray">
                        <span className="text-white font-semibold">{value}×</span> · activo por <span className="text-white font-semibold">{days} días</span>.
                        Visible solo para admins. El usuario no verá el indicador.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white">
                        Cancelar
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="confirm-grant-multiplier"
                        className="bg-p2w-amber text-p2w-bg hover:bg-p2w-amber/90 font-semibold gap-1.5"
                    >
                        <Sparkles className="w-4 h-4" />
                        Activar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
